import React, { memo } from 'react';
import { ClockCircleOutlined, DownSquareFilled } from '@ant-design/icons';
import {
  Button, Popover, Space, Tooltip, Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formatDocumentExpirationDate, statusColor } from '@helpers/index';
import { SCOPES } from '@config/permissionsMap';
import PermissionsGate from '@config/permissionGate';
import { StencilUploadedProps } from './types';
import ReviewDocument from './ReviewDocument';

const WEEK = 7;

const STATUS_COLOR = {
  approved: statusColor.green,
  expired: statusColor.grey,
  rejected: statusColor.red,
  'not-reviewed': statusColor.lightBlue,
  'digital-review': statusColor.blue,
  'digital-review-with-observations': statusColor.orange,
};

const StencilUploaded = ({
  file,
  worker,
  stencil,
  isPopoverOpen,
  actions,
}: StencilUploadedProps) => {
  const { Paragraph } = Typography;
  const { t } = useTranslation();
  const isReviewDisabled = actions.reviewDisabled ?? actions.disabled;
  const isUploadDisabled = actions.uploadDisabled ?? actions.disabled;
  const statusIconStyle = {
    fontSize: '30px',
    color: `${STATUS_COLOR[`${file.status}` as keyof typeof STATUS_COLOR]}`,
  };
  const handlePopoverClick = () => actions.onOpenPopover({
    workerId: worker.id,
    stencilId: file.stencilId,
  });
  const handleReplaceClick = () => actions.onUpload({
    stencil,
    workerId: worker.id,
    indefiniteTerm: file.indefiniteTerm,
    expirable: file.expirable,
  });

  return (
    <Popover
      title={() => {
        if (file.indefiniteTerm) {
          return `${t('utils.sendDocumentation.expires')} : ${t('utils.sendDocumentation.withIndefinitiveTerm')}`;
        }
        if (file.expiresAt) {
          return `${t('utils.sendDocumentation.expires')} : ${formatDocumentExpirationDate(file.expiresAt)}`;
        }
        return `${t('utils.sendDocumentation.expires')} : ${t('utils.sendDocumentation.noExpires')}`;
      }}
      content={(
        <>
          <PermissionsGate scopes={[SCOPES.canApproveDocument]}>
            <Paragraph>
              <ReviewDocument
                file={file}
                worker={worker}
                stencil={stencil}
                disabled={isReviewDisabled}
                onOpenReview={actions.onOpenReview}
              />
            </Paragraph>
          </PermissionsGate>
          <Paragraph>
            <PermissionsGate scopes={[SCOPES.canViewAddDocumentButton]}>
              <Button
                onClick={handleReplaceClick}
                type="link"
                disabled={isUploadDisabled}
              >
                {t('utils.sendDocumentation.replace')}
              </Button>
            </PermissionsGate>
          </Paragraph>
          <Paragraph>
            <Button
              onClick={() => actions.onDetail(file.id, worker.id)}
              type="link"
            >
              {t('utils.sendDocumentation.details')}
            </Button>
          </Paragraph>
          <Paragraph>
            <Button
              onClick={() => actions.onDownload(file.resourceUrl, file.name)}
              type="link"
            >
              {t('utils.sendDocumentation.download')}
            </Button>
          </Paragraph>
          <Paragraph>
            <Button onClick={actions.onClosePopover} type="link">
              {t('utils.sendDocumentation.close')}
            </Button>
          </Paragraph>
        </>
      )}
      trigger="click"
      visible={isPopoverOpen}
    >
      <Space
        size="small"
        direction="vertical"
        className="flex flex-col items-center justify-center"
      >
        <ClockCircleOutlined
          style={{
            fontSize: '10px',
            color: statusColor.red,
            visibility: `${
              dayjs(file.expiresAt).diff(dayjs(), 'day') <= WEEK
                ? 'visible'
                : 'hidden'
            }`,
          }}
        />
        {file.status === 'rejected' ? (
          <Tooltip title={file.revisionComment}>
            <DownSquareFilled style={statusIconStyle} onClick={handlePopoverClick} />
          </Tooltip>
        ) : (
          <DownSquareFilled style={statusIconStyle} onClick={handlePopoverClick} />
        )}
      </Space>
    </Popover>
  );
};

export default memo(StencilUploaded);
